/**
 * @file MovementDocumentLink.tsx
 * @description Ссылка на документ-основание движения склада (phase19.23 этап A).
 *
 * В журнале движений кладовщик спрашивает «откуда это взялось» — номер
 * документа должен открываться в один тап. Маршрут по виду документа берём из
 * реестра: четвёртый вид (перемещение, инвентаризация) добавляется записью, а
 * не веткой `if`.
 *
 * На заказ ссылаемся только при праве `SHOP_VIEW_ORDERS`: без него переход
 * упёрся бы в экран «нет доступа», номер при этом всё равно виден.
 *
 * Тач-цель (phase19.31, приёмка qa4 п. 6–7): ссылка-номер была высотой 17px.
 * Режим `fit`:
 *  - `inline` — как раньше, текст в строке (сводка склада);
 *  - `row` — кнопка-строка во всю ширину высотой `--control-h` (48 на
 *    телефоне, 56 у кладовщика, 44 с 1024); подпись `prefix` входит в неё;
 *  - `cell` — ячейка таблицы: область нажатия `--control-h`, но вид и
 *    высота строки прежние — лишнее «заходит» в поля ячейки (`-my-3`).
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { useHasPermission } from "../../auth/hooks";
import { cn } from "@/lib/utils";
import type {
  StockMovementDocumentDto,
  StockMovementDocumentKind,
} from "../../lib/api-wms";

/** Вид документа → путь в админке магазина. */
const DOCUMENT_ROUTE: Record<
  StockMovementDocumentKind,
  (slug: string, id: string) => string
> = {
  RECEIPT: (slug, id) => `/shop/${slug}/wms/receipts/${id}`,
  WRITEOFF: (slug, id) => `/shop/${slug}/wms/writeoffs/${id}`,
  // phase19.23 этап B — документ «Перемещение» (MV-…).
  TRANSFER: (slug, id) => `/shop/${slug}/wms/transfers/${id}`,
  ORDER: (slug, id) => `/shop/${slug}/orders/${id}`,
};

/** Как ссылка занимает место: в тексте, строкой карточки или в ячейке. */
export type MovementLinkFit = "inline" | "row" | "cell";

/** Реестр раскладок: обёртка ссылки по режиму. */
const FIT_CLASS: Record<MovementLinkFit, string> = {
  inline: "",
  row: "flex min-h-[var(--control-h,3rem)] w-full min-w-0 items-center gap-1 rounded-md",
  cell: "-my-3 inline-flex min-h-[var(--control-h,3rem)] max-w-full items-center",
};

interface Props {
  document?: StockMovementDocumentDto | null;
  /** Slug магазина из маршрута; без него ссылку не построить. */
  slug?: string;
  /** Раскладка; по умолчанию — текст в строке. */
  fit?: MovementLinkFit;
  /** Подпись перед номером («Основание:») — в режиме `row` входит в ссылку. */
  prefix?: ReactNode;
}

/** Номер документа — моно, как все коды. */
const NUMBER_CLASS = "truncate font-mono text-xs";

export function MovementDocumentLink({ document, slug, fit = "inline", prefix }: Props) {
  const canViewOrders = useHasPermission("SHOP_VIEW_ORDERS");
  const wrapCls = fit === "inline" ? "" : FIT_CLASS[fit];

  // Движения без документа существуют (ручная корректировка, резерв канала).
  if (!document) {
    return (
      <span className={cn(wrapCls, "text-on-surface-variant")}>
        {prefix}
        <span>—</span>
      </span>
    );
  }

  const allowed = document.kind !== "ORDER" || canViewOrders;
  if (!slug || !allowed) {
    return (
      <span className={wrapCls}>
        {prefix}
        <span className={NUMBER_CLASS}>{document.number}</span>
      </span>
    );
  }

  return (
    <Link
      to={DOCUMENT_ROUTE[document.kind](slug, document.id)}
      className={cn(
        wrapCls,
        "group underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      {prefix}
      <span className={cn(NUMBER_CLASS, "text-primary group-hover:underline")}>{document.number}</span>
    </Link>
  );
}
